import { Injectable } from '@angular/core';
import {
  Category,
  FaceLandmarker,
  FilesetResolver,
  NormalizedLandmark,
} from '@mediapipe/tasks-vision';

export interface FaceDetectionState {
  detected: boolean;
  centered: boolean;
  multipleFaces: boolean;
  faceWidthRatio: number;
  faceHeightRatio: number;
  faceLeft: number;
  faceRight: number;
  faceTop: number;
  faceBottom: number;
  faceWithinSafeOval: boolean;
  yaw: number;
  pitch: number;
  headPitch3d?: number;
  roll: number;
  leftEyeClosed: boolean;
  rightEyeClosed: boolean;
  mouthOpen: boolean;
  smiling: boolean;
  gazeHorizontal: number;
  gazeVertical: number;
  leftEyeGazeY: number;
  rightEyeGazeY: number;
  leftEyeBlinkScore: number;
  rightEyeBlinkScore: number;
}

export interface FaceDetectionThresholds {
  centeredTolerance: number;
  centeredVerticalTolerance: number;
  yawForward: number;
  yawSide: number;
  pitchDown: number;
  pitchChallenge: number;
  rollSide: number;
  pitchTolerance: number;
  rollTolerance: number;
  minFaceSize: number;
  maxFaceSize: number;
  closeFaceSize: number;
  safeOvalHalfWidth: number;
  safeOvalHalfHeight: number;
  stabilityMs: number;
  holdStillStableMs: number;
  eyesClosedStableMs: number;
  detectionIntervalMs: number;
  countdownSeconds: number;
  eyeClosedBlendshapeThreshold: number;
  mouthOpenBlendshapeThreshold: number;
  smileBlendshapeThreshold: number;
  gazeHorizontalThreshold: number;
  gazeVerticalThreshold: number;
}

export const FACE_CAPTURE_THRESHOLDS: FaceDetectionThresholds = {
  centeredTolerance: 0.12,
  centeredVerticalTolerance: 0.16,
  yawForward: 12,
  yawSide: 16,
  pitchDown: 14,
  pitchChallenge: 8,
  rollSide: 16,
  pitchTolerance: 14,
  rollTolerance: 15,
  minFaceSize: 0.24,
  maxFaceSize: 0.78,
  closeFaceSize: 0.42,
  safeOvalHalfWidth: 0.21,
  safeOvalHalfHeight: 0.4,
  stabilityMs: 2000,
  holdStillStableMs: 1500,
  eyesClosedStableMs: 450,
  detectionIntervalMs: 80,
  countdownSeconds: 3,
  eyeClosedBlendshapeThreshold: 0.55,
  mouthOpenBlendshapeThreshold: 0.45,
  smileBlendshapeThreshold: 0.45,
  gazeHorizontalThreshold: 0.2,
  gazeVerticalThreshold: 0.2,
};

export const EMPTY_FACE_DETECTION: FaceDetectionState = {
  detected: false,
  centered: false,
  multipleFaces: false,
  faceWidthRatio: 0,
  faceHeightRatio: 0,
  faceLeft: 0.5,
  faceRight: 0.5,
  faceTop: 0.5,
  faceBottom: 0.5,
  faceWithinSafeOval: false,
  yaw: 0,
  pitch: 0,
  roll: 0,
  leftEyeClosed: false,
  rightEyeClosed: false,
  mouthOpen: false,
  smiling: false,
  gazeHorizontal: 0,
  gazeVertical: 0,
  leftEyeGazeY: 0,
  rightEyeGazeY: 0,
  leftEyeBlinkScore: 0,
  rightEyeBlinkScore: 0,
};

const FACE_LANDMARKER_MODEL =
  'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task';
const VISION_WASM_PATH = 'assets/mediapipe/wasm';

@Injectable({ providedIn: 'root' })
export class FaceDetectionService {
  private landmarker: FaceLandmarker | null = null;
  private initialization: Promise<void> | null = null;

  async initialize(): Promise<void> {
    if (this.landmarker) return;
    if (this.initialization) return this.initialization;

    this.initialization = this.createLandmarker();
    try {
      await this.initialization;
    } catch (error) {
      this.initialization = null;
      throw error;
    }
  }

  detect(video: HTMLVideoElement, timestamp: number): FaceDetectionState {
    if (!this.landmarker) throw new Error('Face Landmarker no esta inicializado.');

    const result = this.landmarker.detectForVideo(video, timestamp);
    const faces = result.faceLandmarks;
    if (faces.length === 0) return EMPTY_FACE_DETECTION;

    const blendshapes = result.faceBlendshapes?.[0]?.categories ?? [];
    return this.normalizeFace(faces[0], faces.length > 1, blendshapes);
  }

  close(): void {
    this.landmarker?.close();
    this.landmarker = null;
    this.initialization = null;
  }

  private async createLandmarker(): Promise<void> {
    const fileset = await FilesetResolver.forVisionTasks(VISION_WASM_PATH);
    this.landmarker = await FaceLandmarker.createFromOptions(fileset, {
      baseOptions: {
        modelAssetPath: FACE_LANDMARKER_MODEL,
        delegate: 'GPU',
      },
      runningMode: 'VIDEO',
      numFaces: 2,
      minFaceDetectionConfidence: 0.5,
      minFacePresenceConfidence: 0.5,
      minTrackingConfidence: 0.5,
      outputFaceBlendshapes: true,
    });
  }

  private normalizeFace(
    landmarks: NormalizedLandmark[],
    multipleFaces: boolean,
    blendshapes: Category[],
  ): FaceDetectionState {
    const left = this.landmark(landmarks, 234);
    const right = this.landmark(landmarks, 454);
    const forehead = this.landmark(landmarks, 10);
    const chin = this.landmark(landmarks, 152);
    const nose = this.landmark(landmarks, 1);
    const leftEye = this.landmark(landmarks, 33);
    const rightEye = this.landmark(landmarks, 263);
    const leftEyeInner = this.landmark(landmarks, 133);
    const rightEyeInner = this.landmark(landmarks, 362);
    const leftEyeTop = this.landmark(landmarks, 159);
    const leftEyeBottom = this.landmark(landmarks, 145);
    const rightEyeTop = this.landmark(landmarks, 386);
    const rightEyeBottom = this.landmark(landmarks, 374);
    const leftIris = this.landmark(landmarks, 468);
    const rightIris = this.landmark(landmarks, 473);

    const faceWidthRatio = Math.abs(right.x - left.x);
    const faceCenterX = (left.x + right.x) / 2;
    const faceCenterY = (forehead.y + chin.y) / 2;

    const faceLeft = Math.min(left.x, right.x);
    const faceRight = Math.max(left.x, right.x);
    const faceTop = Math.min(forehead.y, chin.y);
    const faceBottom = Math.max(forehead.y, chin.y);
    const faceHeightRatio = faceBottom - faceTop;

    const safeOval = {
      left: 0.5 - FACE_CAPTURE_THRESHOLDS.safeOvalHalfWidth,
      right: 0.5 + FACE_CAPTURE_THRESHOLDS.safeOvalHalfWidth,
      top: 0.49 - FACE_CAPTURE_THRESHOLDS.safeOvalHalfHeight,
      bottom: 0.49 + FACE_CAPTURE_THRESHOLDS.safeOvalHalfHeight,
    };
    const faceWithinSafeOval =
      faceLeft >= safeOval.left &&
      faceRight <= safeOval.right &&
      faceTop >= safeOval.top &&
      faceBottom <= safeOval.bottom;

    const eyeCenterX = (leftEye.x + rightEye.x) / 2;
    const eyeDistance = Math.max(Math.abs(rightEye.x - leftEye.x), 0.001);
    const yaw = ((nose.x - eyeCenterX) / eyeDistance) * 45;
    const pitch = ((nose.y - (forehead.y + chin.y) / 2) / Math.max(Math.abs(chin.y - forehead.y), 0.001)) * 45;
    const eyeMidpoint = {
      x: eyeCenterX,
      y: (leftEye.y + rightEye.y) / 2,
      z: (leftEye.z + rightEye.z) / 2,
    };
    const noseBridge = this.landmark(landmarks, 168);
    const depthToNose = nose.z - noseBridge.z;
    const verticalToChin = chin.y - eyeMidpoint.y;
    const headPitch3d = Math.atan2(
      (chin.z - eyeMidpoint.z) - depthToNose,
      verticalToChin,
    ) * 180 / Math.PI;
    const roll = (Math.atan2(rightEye.y - leftEye.y, rightEye.x - leftEye.x) * 180) / Math.PI;

    const leftEyeBlinkScore = this.blendshapeScore(blendshapes, 'eyeBlinkLeft');
    const rightEyeBlinkScore = this.blendshapeScore(blendshapes, 'eyeBlinkRight');
    const jawOpenScore = this.blendshapeScore(blendshapes, 'jawOpen');
    const smileScore = Math.max(
      this.blendshapeScore(blendshapes, 'mouthSmileLeft'),
      this.blendshapeScore(blendshapes, 'mouthSmileRight'),
    );
    const gazeHorizontal = (
      this.relativeEyePosition(leftIris.x, leftEye.x, leftEyeInner.x) +
      this.relativeEyePosition(rightIris.x, rightEye.x, rightEyeInner.x)
    ) / 2;
    const leftEyeGazeY = this.relativeEyePosition(leftIris.y, leftEyeTop.y, leftEyeBottom.y);
    const rightEyeGazeY = this.relativeEyePosition(rightIris.y, rightEyeTop.y, rightEyeBottom.y);
    const gazeVertical = (leftEyeGazeY + rightEyeGazeY) / 2;

    return {
      detected: true,
      centered:
        Math.abs(faceCenterX - 0.5) <= FACE_CAPTURE_THRESHOLDS.centeredTolerance &&
        Math.abs(faceCenterY - 0.5) <= FACE_CAPTURE_THRESHOLDS.centeredVerticalTolerance,
      multipleFaces,
      faceWidthRatio,
      faceHeightRatio,
      faceLeft,
      faceRight,
      faceTop,
      faceBottom,
      faceWithinSafeOval,
      yaw,
      pitch,
      headPitch3d,
      roll,
      leftEyeClosed: leftEyeBlinkScore >= FACE_CAPTURE_THRESHOLDS.eyeClosedBlendshapeThreshold,
      rightEyeClosed: rightEyeBlinkScore >= FACE_CAPTURE_THRESHOLDS.eyeClosedBlendshapeThreshold,
      mouthOpen: jawOpenScore >= FACE_CAPTURE_THRESHOLDS.mouthOpenBlendshapeThreshold,
      smiling: smileScore >= FACE_CAPTURE_THRESHOLDS.smileBlendshapeThreshold,
      gazeHorizontal,
      gazeVertical,
      leftEyeGazeY,
      rightEyeGazeY,
      leftEyeBlinkScore,
      rightEyeBlinkScore,
    };
  }

  private blendshapeScore(categories: Category[], categoryName: string): number {
    return categories.find((category) => category.categoryName === categoryName)?.score ?? 0;
  }

  private relativeEyePosition(value: number, start: number, end: number): number {
    const center = (start + end) / 2;
    const halfRange = Math.max(Math.abs(end - start) / 2, 0.001);
    return (value - center) / halfRange;
  }

  private landmark(landmarks: NormalizedLandmark[], index: number): NormalizedLandmark {
    return landmarks[index] ?? { x: 0.5, y: 0.5, z: 0, visibility: 1 };
  }
}
